import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Mail, Phone, MoreVertical, Trash2, ChevronLeft, ChevronRight, Search, X, User, Building2, UserCheck, ArrowRightLeft } from 'lucide-react'
import { toast } from 'sonner'
import { crmApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDate } from '@/lib/utils'
import type { Lead, LeadSource, LeadStatus } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_SOURCES = '__all__'
const ALL_STATUSES = '__all__'

interface LeadFilters {
  searchTerm: string
  sourceId: string
  statusId: string
}

export default function LeadsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [convertDialogOpen, setConvertDialogOpen] = useState(false)
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null)
  const [leadToConvert, setLeadToConvert] = useState<Lead | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const [filters, setFilters] = useState<LeadFilters>({
    searchTerm: '',
    sourceId: ALL_SOURCES,
    statusId: ALL_STATUSES,
  })

  // Fetch leads with server-side pagination
  const { data: leadsResponse, isLoading, error } = useQuery({
    queryKey: ['leads', currentPage, pageSize, filters],
    queryFn: () => crmApi.getLeads({
      page: currentPage,
      page_size: pageSize,
      search: filters.searchTerm || undefined,
      source_id: filters.sourceId !== ALL_SOURCES ? filters.sourceId : undefined,
      status_id: filters.statusId !== ALL_STATUSES ? filters.statusId : undefined,
    }),
  })

  const { data: sources = [] } = useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => crmApi.getLeadSources(true),
  })

  const { data: statuses = [] } = useQuery({
    queryKey: ['lead-statuses'],
    queryFn: () => crmApi.getLeadStatuses(true),
  })

  const leads = leadsResponse?.leads || []
  const totalRecords = leadsResponse?.total || 0
  const totalPages = leadsResponse?.total_pages || 0

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      sourceId: ALL_SOURCES,
      statusId: ALL_STATUSES,
    })
    setCurrentPage(1)
  }

  const hasActiveFilters = filters.searchTerm ||
    filters.sourceId !== ALL_SOURCES ||
    filters.statusId !== ALL_STATUSES

  const deleteMutation = useMutation({
    mutationFn: (leadId: string) => crmApi.deleteLead(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      toast.success(t('messages.deleteSuccess'))
      setDeleteDialogOpen(false)
      setLeadToDelete(null)
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, t('messages.deleteError')))
    },
  })

  const convertMutation = useMutation({
    mutationFn: (leadId: string) => crmApi.convertLead(leadId, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      toast.success(t('crm.leads.convertSuccess'))
      setConvertDialogOpen(false)
      setLeadToConvert(null)
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, t('crm.leads.convertError')))
    },
  })

  const handleDeleteClick = (lead: Lead) => {
    setLeadToDelete(lead)
    setDeleteDialogOpen(true)
  }

  const handleConvertClick = (lead: Lead) => {
    setLeadToConvert(lead)
    setConvertDialogOpen(true)
  }

  const confirmDelete = () => {
    if (leadToDelete) {
      deleteMutation.mutate(leadToDelete.lead_id)
    }
  }

  const confirmConvert = () => {
    if (leadToConvert) {
      convertMutation.mutate(leadToConvert.lead_id)
    }
  }

  const getLeadName = (lead: Lead) => {
    return lead.last_name ? `${lead.first_name} ${lead.last_name}` : lead.first_name
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">{t('common.failedToLoad')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between -mt-2">
        <div>
          <h1 className="text-2xl font-bold">{t('crm.leads.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('crm.leads.subtitle')}
          </p>
        </div>
        {canAddEdit('CRM_LEADS') && (
          <Link to="/crm/leads/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('crm.leads.addLead')}
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('crm.leads.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              setCurrentPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.sourceId}
          onValueChange={(value) => {
            setFilters({ ...filters, sourceId: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('crm.leads.allSources')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_SOURCES}>{t('crm.leads.allSources')}</SelectItem>
            {sources.map((source: LeadSource) => (
              <SelectItem key={source.source_id} value={source.source_id}>
                {source.source_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.statusId}
          onValueChange={(value) => {
            setFilters({ ...filters, statusId: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('crm.leads.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('crm.leads.allStatuses')}</SelectItem>
            {statuses.map((status: LeadStatus) => (
              <SelectItem key={status.status_id} value={status.status_id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: status.status_color }}
                  />
                  {status.status_name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      {totalRecords === 0 && !hasActiveFilters ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('crm.leads.noLeadsYet')}
            </div>
            {canAddEdit('CRM_LEADS') && (
              <Link to="/crm/leads/new">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('crm.leads.addLead')}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : totalRecords === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('crm.leads.noMatchingLeads')}
            </div>
            <Button variant="outline" onClick={clearFilters}>
              <X className="mr-2 h-4 w-4" />
              {t('common.reset')} {t('common.filters')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col h-[calc(100vh-240px)]">
          <Card className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>{t('crm.leads.name')}</TableHead>
                    <TableHead>{t('common.email')}</TableHead>
                    <TableHead>{t('common.phone')}</TableHead>
                    <TableHead>{t('crm.leads.company')}</TableHead>
                    <TableHead>{t('crm.leads.source')}</TableHead>
                    <TableHead>{t('crm.leads.status')}</TableHead>
                    <TableHead>{t('crm.leads.score')}</TableHead>
                    <TableHead>{t('common.create')}</TableHead>
                    <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead: Lead) => (
                    <TableRow key={lead.lead_id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {getLeadName(lead)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {lead.email ? (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {lead.email}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {lead.phone ? (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            {lead.phone}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {lead.company_name ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            {lead.company_name}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {lead.source_name || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        {lead.status_name ? (
                          <Badge
                            variant="outline"
                            style={{
                              borderColor: lead.status_color,
                              color: lead.status_color,
                            }}
                          >
                            {lead.status_name}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={lead.lead_score >= 70 ? 'default' : lead.lead_score >= 40 ? 'secondary' : 'outline'}>
                          {lead.lead_score}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(lead.created_at)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/crm/leads/${lead.lead_id}`)}>
                              <User className="mr-2 h-4 w-4" />
                              {t('common.view')}
                            </DropdownMenuItem>
                            {canAddEdit('CRM_LEADS') && (
                              <DropdownMenuItem onClick={() => navigate(`/crm/leads/${lead.lead_id}/edit`)}>
                                <UserCheck className="mr-2 h-4 w-4" />
                                {t('common.edit')}
                              </DropdownMenuItem>
                            )}
                            {canAddEdit('CRM_LEADS') && !lead.converted_client_id && (
                              <DropdownMenuItem onClick={() => handleConvertClick(lead)}>
                                <ArrowRightLeft className="mr-2 h-4 w-4" />
                                {t('crm.leads.convert')}
                              </DropdownMenuItem>
                            )}
                            {canAddEdit('CRM_LEADS') && <DropdownMenuSeparator />}
                            {canAddEdit('CRM_LEADS') && (
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDeleteClick(lead)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t('common.delete')}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination Controls */}
            <div className="flex items-center gap-3 px-4 py-2 border-t">
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[70px] h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                {startRecord}-{endRecord} {t('common.of')} {totalRecords}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.delete')} {t('crm.leads.lead')}</DialogTitle>
            <DialogDescription>
              {t('messages.confirmDelete')} <strong>{leadToDelete && getLeadName(leadToDelete)}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t('common.loading') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Confirmation Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crm.leads.convertToClient')}</DialogTitle>
            <DialogDescription>
              {t('crm.leads.convertConfirmation')} <strong>{leadToConvert && getLeadName(leadToConvert)}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={confirmConvert}
              disabled={convertMutation.isPending}
            >
              {convertMutation.isPending ? t('common.loading') : t('crm.leads.convert')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

